import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getBrandDocumentMeta, resolveBrandId } from '../branding/brandAssets';

/**
 * Syncs document title, meta tags, and favicons with the current route (VMS vs IMS vs neutral).
 */
function BrandDocumentHead() {
  const { pathname, search } = useLocation();
  const brandId = resolveBrandId(pathname, search);
  const meta = useMemo(() => getBrandDocumentMeta(brandId), [brandId]);

  useEffect(() => {
    document.title = meta.title;

    const setMeta = (selector: string, attr: 'content', value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', meta.description);
    setMeta('meta[name="application-name"]', 'content', meta.applicationName);
    setMeta('meta[name="theme-color"]', 'content', meta.themeColor);

    setMeta('meta[property="og:site_name"]', 'content', meta.ogSiteName);
    setMeta('meta[property="og:title"]', 'content', meta.ogTitle);
    setMeta('meta[property="og:description"]', 'content', meta.ogDescription);
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      const origin = window.location.origin;
      ogImage.setAttribute('content', `${origin}${meta.icon72}`);
    }

    setMeta('meta[name="twitter:title"]', 'content', meta.twitterTitle);
    setMeta('meta[name="twitter:description"]', 'content', meta.twitterDescription);

    const f16 = document.getElementById('brand-favicon-16') as HTMLLinkElement | null;
    const f32 = document.getElementById('brand-favicon-32') as HTMLLinkElement | null;
    const apple = document.getElementById('brand-apple-touch-icon') as HTMLLinkElement | null;
    if (f16) f16.href = meta.favicon16;
    if (f32) f32.href = meta.favicon32;
    if (apple) apple.href = meta.icon72;
  }, [meta]);

  return null;
}

export default BrandDocumentHead;
